// 채널 선택(channel_selection) wire 계약의 경계다.
//
// topic 값은 3-상태다. 이 파일은 이 상태를 구분하려고 있다:
//   - null: **위임**. BE 가 제출 시점의 기본 제안으로 다시 조립한다.
//   - []:   **명시적 빈 선택**. BE 가 그 topic 의 재조립을 건너뛴다. 그래서 config 원본
//           mux 선언이 그대로 제출된다.
//   - [a,b]: 그 채널들을 선택한다.
// 값이 없을 때(제안에 없음) []로 바꾸면 "위임"이 "명시적 빈 선택"이 된다. 그러면 config 원본
// mux 가 데이터셋과 맞지 않는 상태로 제출된다(DT 데이터셋 제출을 막는 경로). 폼과 에디터는
// 이 파일의 헬퍼로만 값을 만들고 고친다. 호출부마다 3-상태 해석을 다시 구현하지 않는다.
//
// Owns:
// - `ChannelSelection` wire 타입과 topic 축, 3-상태의 의미.
// - 제안을 선택 시드로 변환하는 규칙(없는 topic 은 null 유지, 모든 topic 이 없으면 시드 없음).
// - 채널 토글 invariant(위임 topic 에서 켜면 선택으로 바꾸고, 비워도 null 로 바꾸지 않는다).
// - 3-상태 해석(체크 집합·topic 별 선택 수).
//
// Does not own:
// - 제안할 채널(BE `suggest_channel_selection`)과 토글 후보 목록(데이터셋 구조).
// - payload 조립과 전송(폼 빌더와 api 함수가 값을 그대로 보낸다).

export const CHANNEL_SELECTION_TOPICS = [
  "observation_image",
  "observation_vector",
  "action",
] as const;

export type ChannelSelectionTopic = (typeof CHANNEL_SELECTION_TOPICS)[number];

// topic 별 최종 mux 채널 선택이다(매핑 UI 의 결과). 값의 3-상태 의미는 파일 첫 주석을 본다.
export type ChannelSelection = Record<ChannelSelectionTopic, string[] | null>;

/**
 * BE 제안(suggested_selection)을 폼 선택 시드로 바꾼다.
 *
 * 제안 dict 에 없는 topic 은 null(위임)로 둔다. 그 topic 은 config mux 에 없거나, BE 가
 * 제안을 만들 수 없었던 topic 이다. []로 채우면 나중에 config 가 바뀌어도 "명시적 빈 선택"으로
 * 고정된다. 제안 전체가 비면(config 미확보·공통 채널 없음) null 을 반환하고 시드하지 않는다.
 * 선택이 null 이면 제출은 모든 topic 을 위임한다.
 *
 * Args:
 *   suggestion: preview 응답의 `suggested_selection`. 예) `{ observation_image: ["cam"] }`
 *
 * Returns:
 *   예) `{ observation_image: ["cam"], observation_vector: null, action: null }`.
 *   제안이 비면 null.
 */
export function channelSelectionFromSuggestion(
  suggestion: Readonly<Record<string, readonly string[]>>,
): ChannelSelection | null {
  if (!CHANNEL_SELECTION_TOPICS.some((topic) => topic in suggestion)) {
    return null;
  }
  return {
    observation_image:
      suggestion["observation_image"] === undefined ? null : [...suggestion["observation_image"]],
    observation_vector:
      suggestion["observation_vector"] === undefined ? null : [...suggestion["observation_vector"]],
    action: suggestion["action"] === undefined ? null : [...suggestion["action"]],
  };
}

/**
 * 채널을 토글한다. 매핑 UI 는 이 함수로만 선택을 수정한다.
 *
 * 위임(null) topic 에서 채널을 켜면 그 채널 하나를 가진 선택으로 **바꾼다**(빈 배열을 거치지 않는다).
 * 마지막 채널을 꺼도 []로 둔다. 사용자가 비운 선택은 위임이 아니라 명시적 빈 선택이다.
 */
export function toggleSelectedChannel(
  selection: ChannelSelection,
  topic: ChannelSelectionTopic,
  name: string,
): ChannelSelection {
  const names = selection[topic];
  if (names === null) {
    return { ...selection, [topic]: [name] };
  }
  const next = names.includes(name)
    ? names.filter((item) => item !== name)
    : [...names, name].sort();
  return { ...selection, [topic]: next };
}

// 매핑 UI 의 체크 집합이다. 위임(null) topic 은 체크 없음으로 표시한다. 그 topic 에 들어갈
// 채널은 BE 제안이 소유한다. 그래서 화면이 값을 만들지 않는다. 이 null→빈 변환은 표시에만 쓴다.
export function checkedChannelNames(
  selection: ChannelSelection,
): Readonly<Partial<Record<string, readonly string[]>>> {
  const checked: Partial<Record<string, readonly string[]>> = {};
  for (const topic of CHANNEL_SELECTION_TOPICS) {
    const names = selection[topic];
    if (names !== null) {
      checked[topic] = names;
    }
  }
  return checked;
}

// topic 별 선택 수다. null 은 위임이다. "0개 선택"과 다르므로 수로 바꾸지 않는다.
// 표시 방법은 호출부가 정한다.
export function selectedChannelCount(
  selection: ChannelSelection,
  topic: ChannelSelectionTopic,
): number | null {
  const names = selection[topic];
  return names === null ? null : names.length;
}
